import React from 'react';

interface BadgeProps {
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  children,
  className = '',
  style = {},
  rounded = true,
}) => {
  const getVariantStyles = (): React.CSSProperties => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: 'var(--accent)',
          color: 'white',
          ...style,
        };
      case 'secondary':
        return {
          backgroundColor: 'var(--bg-secondary)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border)',
          ...style,
        };
      case 'success':
        return {
          backgroundColor: '#22c55e',
          color: 'white',
          ...style,
        };
      case 'warning':
        return {
          backgroundColor: '#f59e0b',
          color: 'white',
          ...style,
        };
      case 'error':
        return {
          backgroundColor: '#ef4444',
          color: 'white',
          ...style,
        };
      case 'info':
        return {
          backgroundColor: 'var(--accent)',
          color: 'white',
          ...style,
        };
      default:
        return {
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-secondary)',
          ...style,
        };
    }
  };

  // Renders correctly centered in every real browser with plain, symmetric
  // padding — no flex/table centering trick needed here. html2canvas (used
  // for the invoice PDF download) has its own separate baseline-placement
  // quirk that pixel-measurement confirmed sits this text low in its box
  // regardless of what centering technique is used; that's compensated
  // only in the capture path (InvoiceSharePage's handleDownload, via
  // `.pdf-capturing`), not here, so this component's normal rendering
  // stays untouched. `children` is wrapped in its own inline span so that
  // capture-time fix has something to transform without moving this
  // badge's background box along with it.
  const getSizeClasses = (): string => {
    switch (size) {
      case 'sm':
        return 'px-2 py-1 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  const badgeClasses = [
    'inline-block font-medium whitespace-nowrap',
    getSizeClasses(),
    rounded ? 'rounded-full' : 'rounded-md',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={badgeClasses} style={getVariantStyles()}>
      <span className="badge-text">{children}</span>
    </span>
  );
};

export default Badge;
